import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { isoDateStringValidator } from '../../../../../validators/iso-date-string-validator';
import { maxFileSizeValidator } from '../../../../shared/components/form-components/file-uploader/file-upload-field/max-file-size-validator';
import { minimumAgeValidator } from '../../../../../validators/minimum-age.validator';
import { sinValidator } from '../../../../../validators/valid-sin.validator';
import { phoneNumberWithCountryValidator } from '../../../../../validators/phone-number-with-country-validator';
import { emailValidator } from '../../../../../validators/email.validator';
import { passwordValidator, PasswordValidatorOpts } from '../../../../../validators/password.validator';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { fieldMatchValidator } from '../../../../../validators/field-match.validator';
import { MpSelfEnrolmentRequest } from '../../../../../models/users/requests/mp-self-enrolment-request';
import { Address } from '../../../../../models/users/dto/address';
import { MediaUtils } from '../../../../../utils/media-utils';
import { CreateAttachmentRequest } from '../../../../../models/users/requests/create-attachment-request';
import { FileUtils } from '../../../../../utils/file-utils';
import { map } from 'rxjs/operators';
import { UserFile } from '../../../../../models/users/dto/user-file';
import { Subscribable } from '../../../../../models/base/subscribable';
import { AddressFormFactory } from '../../../../../form-factories/address-form-factory';

@Injectable()
export class MpSelfEnrolmentFormService extends Subscribable {
  private _emailInUse = new BehaviorSubject(false);
  public emailInUse$ = this._emailInUse as BehaviorSubject<boolean>;
  public setEmailInUse(inUse: boolean): void {
    this._emailInUse.next(inUse);
  }

  private listenToEmailInUse = this.emailInUse$.subscribeWhileAlive({
    owner: this,
    next: inUse => {
      const emailControl = this.loginDetailsForm?.controls?.email;
      if (inUse) {
        emailControl?.setErrors({ emailInUse: true });
      } else {
        if (emailControl?.hasError('emailInUse')) {
          emailControl?.setErrors(null);
        }
      }
    }
  });

  public personalInfoForm = new FormGroup({
    firstName: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    middleName: new FormControl<string>(''),
    lastName: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    preferredName: new FormControl<string>(''),
    socialInsuranceNumber: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, sinValidator]
    }),
    birthDate: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, isoDateStringValidator, minimumAgeValidator(18)]
    }),
    genderId: new FormControl<number | null>(null, { validators: [Validators.required] }),
    pronounId: new FormControl<number | null>(null, { validators: [Validators.required] }),
    files: new FormControl<File[] | null>(null, { validators: [Validators.required, maxFileSizeValidator(3)] }),
    allowThirdPartyContributions: new FormControl<boolean>(false, { nonNullable: true })
  });

  public contactInfoForm = new FormGroup(
    {
      primaryPhoneNumber: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required]
      }),
      primaryPhoneNumberCountryCode: new FormControl<string>('CA', {
        nonNullable: true,
        validators: [Validators.required]
      }),
      secondaryPhoneNumber: new FormControl<string>(''),
      secondaryPhoneNumberCountryCode: new FormControl<string>('CA'),
      address: AddressFormFactory.createAddressForm()
    },
    {
      validators: [phoneNumberWithCountryValidator('primaryPhoneNumber', 'primaryPhoneNumberCountryCode')]
    }
  );

  private passwordOpts: PasswordValidatorOpts = {
    requireUppercase: true,
    requireLowercase: true,
    requireNumber: true,
    requireSymbol: true,
    minLength: 8,
    maxLength: 50
  };

  public loginDetailsForm = new FormGroup(
    {
      email: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, emailValidator]
      }),
      password: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, passwordValidator(this.passwordOpts)]
      }),
      confirmPassword: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required]
      }),
      acceptTerms: new FormControl<boolean>(false, { validators: [Validators.requiredTrue] })
    },
    { validators: fieldMatchValidator('password', 'confirmPassword', 'passwordsMustMatch') }
  );

  public constructRequestObject(): Observable<{ request: MpSelfEnrolmentRequest; files: UserFile[] }> {
    const personal = this.personalInfoForm.getRawValue();
    const contact = this.contactInfoForm.getRawValue();
    const login = this.loginDetailsForm.getRawValue();

    const request = new MpSelfEnrolmentRequest();

    // Personal info
    request.firstName = personal.firstName;
    request.middleName = personal.middleName || null;
    request.lastName = personal.lastName;
    request.preferredName = personal.preferredName || null;
    request.socialInsuranceNumber = personal.socialInsuranceNumber;
    request.birthDate = personal.birthDate;
    request.genderId = personal.genderId ?? -1;
    request.pronounId = personal.pronounId ?? -1;
    request.allowThirdPartyContributions = personal.allowThirdPartyContributions;

    // // Login details
    request.email = login.email;
    request.password = login.password;

    // Contact info
    request.primaryPhoneNumber = contact.primaryPhoneNumber;
    request.primaryPhoneNumberCountryCode = contact.primaryPhoneNumberCountryCode;

    request.secondaryPhoneNumber = contact.secondaryPhoneNumber || null;
    request.secondaryPhoneNumberCountryCode = contact.secondaryPhoneNumber
      ? contact.secondaryPhoneNumberCountryCode
      : null;

    // Address
    const address = new Address();
    address.addressLine1 = contact.address.addressLine1 ?? '';
    address.addressLine2 = contact.address.addressLine2 ?? '';
    address.addressLine3 = contact.address.addressLine3 ?? '';
    address.city = contact.address.city ?? '';
    address.stateId = contact.address.stateId ?? -1;
    address.countryId = contact.address.countryId ?? -1;
    address.postalCode = contact.address.postalCode ?? '';
    request.address = address;

    // Attachments
    const files = personal.files || [];
    request.attachments =
      files?.map(file => {
        return new CreateAttachmentRequest(FileUtils.getAttachmentTypeFromFile(file), file);
      }) ?? [];
    return from(Promise.all(files.map(file => MediaUtils.fileToUserFile(file)))).pipe(
      map(userFiles => ({
        request,
        files: userFiles ?? []
      }))
    );
  }
}
